import React, { useState, useEffect, useMemo } from 'react';
import { Card, Spinner, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import apiService from '../services/api';

const HourlyUnsuccessfulTrafficTable = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Extract hour columns dynamically from the first data row
    const hourColumns = useMemo(() => {
        if (!data || data.length === 0) return [];

        // Get all keys from the first row and filter out known metadata columns
        const allKeys = Object.keys(data[0]);
        return allKeys.filter(key =>
            key !== 'organization_name' &&
            key !== 'organization_code' &&
            key !== 'total_logins'
        );
    }, [data]);

    useEffect(() => {
        loadHourlyTrafficData();
    }, []);

    const loadHourlyTrafficData = async () => {
        setLoading(true);
        setError('');

        try {
            const response = await apiService.get('/enterprise/technical/hourly_unsuccessful_platform_login_attempts');

            if (response.success) {
                setData(response.data || []);
            } else {
                console.log(response);
                setError(response.message || 'Failed to load hourly unsuccessful traffic data');
            }
        } catch (err) {
            console.error('Error loading hourly unsuccessful traffic data:', err);
            setError('Failed to load hourly unsuccessful traffic data');
        } finally {
            setLoading(false);
        }
    };

    // Calculate totals row
    const totals = useMemo(() => {
        if (!data || data.length === 0) return null;

        const totalRow = { organization_name: 'Total', organization_code: '' };
        hourColumns.forEach(hour => {
            totalRow[hour] = data.reduce((sum, row) => sum + (parseInt(row[hour]) || 0), 0);
        });
        totalRow.total_logins = data.reduce((sum, row) => sum + (parseInt(row.total_logins) || 0), 0);

        return totalRow;
    }, [data]);

    // Find peak traffic for each row for highlighting
    const getPeakValue = (row) => {
        return Math.max(...hourColumns.map(hour => parseInt(row[hour]) || 0));
    };

    // Get background color based on value relative to peak
    const getCellColor = (value, peakValue) => {
        if (value === 0 || peakValue === 0) return 'bg-light';
        const ratio = value / peakValue;
        if (ratio === 1) return 'bg-danger bg-opacity-25 fw-semibold';
        if (ratio >= 0.7) return 'bg-warning bg-opacity-25';
        if (ratio >= 0.4) return 'bg-warning bg-opacity-10';
        return 'bg-light';
    };

    if (loading) {
        return (
            <Card>
                <Card.Body className="text-center py-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                    <div className="mt-2">Loading hourly unsuccessful traffic data...</div>
                </Card.Body>
            </Card>
        );
    }

    if (error) {
        return (
            <Card>
                <Card.Body>
                    <Alert variant="danger">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        {error}
                    </Alert>
                </Card.Body>
            </Card>
        );
    }

    if (!data || data.length === 0) {
        return (
            <Card>
                <Card.Body className="text-center py-4 text-muted">
                    <i className="bi bi-inbox display-4"></i>
                    <div className="mt-2">No hourly unsuccessful traffic data available</div>
                </Card.Body>
            </Card>
        );
    }

    return (
        <Card>
            <Card.Header className="bg-danger text-white">
                <h5 className="mb-0">
                    <i className="bi bi-shield-exclamation me-2"></i>
                    Failed Logins(Past 24 Hrs)
                    <OverlayTrigger
                        placement="right"
                        overlay={
                            <Tooltip>
                                Peak failed login hours are highlighted in red, high traffic in orange, moderate in yellow
                            </Tooltip>
                        }
                    >
                        <i className="bi bi-info-circle ms-2" style={{ cursor: 'pointer' }}></i>
                    </OverlayTrigger>
                </h5>
            </Card.Header>
            <Card.Body className="p-0">
                <div className="table-responsive">
                    <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.85rem' }}>
                        <thead className="table-secondary">
                            <tr>
                                <th className="sticky-col" style={{ position: 'sticky', left: 0, zIndex: 10, background: '#e9ecef' }}>Organization</th>
                                <th>Code</th>
                                {hourColumns.map(hour => (
                                    <th key={hour} className="text-center" style={{ minWidth: '50px' }}>{hour}</th>
                                ))}
                                <th className="text-center bg-danger bg-opacity-10">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((row, idx) => {
                                const peakValue = getPeakValue(row);
                                return (
                                    <tr key={idx}>
                                        <td className="sticky-col fw-medium" style={{ position: 'sticky', left: 0, zIndex: 9, background: 'white' }}>
                                            {row.organization_name}
                                        </td>
                                        <td className="text-muted">
                                            {row.organization_code}
                                        </td>
                                        {hourColumns.map(hour => {
                                            const value = parseInt(row[hour]) || 0;
                                            return (
                                                <td
                                                    key={hour}
                                                    className={`text-center ${getCellColor(value, peakValue)}`}
                                                >
                                                    {value}
                                                </td>
                                            );
                                        })}
                                        <td className="text-center fw-semibold bg-danger bg-opacity-10">
                                            {row.total_logins}
                                        </td>
                                    </tr>
                                );
                            })}
                            {totals && (
                                <tr className="table-primary fw-bold">
                                    <td className="sticky-col" style={{ position: 'sticky', left: 0, zIndex: 9 }}>
                                        {totals.organization_name}
                                    </td>
                                    <td></td>
                                    {hourColumns.map(hour => (
                                        <td key={hour} className="text-center">
                                            {totals[hour]}
                                        </td>
                                    ))}
                                    <td className="text-center bg-danger bg-opacity-25">
                                        {totals.total_logins}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Card.Body>
        </Card>
    );
};

export default HourlyUnsuccessfulTrafficTable;
